import { ProductCategoryType } from '@bcube/shared';
import { ControlCodes } from '@bcube/shared/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { productApi } from '@/api/commercial/products';
import {
  CreateCategoryDialog,
  CreateProductDialog,
  DeleteCategoryDialog,
  DeleteProductDialog,
  EditCategoryDialog,
  EditProductDialog,
} from '@/components/dialogs/commercial/products';
import { ProductCategoryTable } from '@/components/table/commercial/product-category-table';
import { ProductTable } from '@/components/table/commercial/product-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TableSkeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermissions } from '@/hooks/use-permissions';
import { useToast } from '@/hooks/use-toast';
import type { IProduct, IProductCategory } from '@/types/commercial/product';

export function ProductListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();

  // Permissions
  const canCreateProduct = hasPermission(ControlCodes.commercial_product_create);
  const canCreateCategory = hasPermission(ControlCodes.commercial_product_category_create);

  // Tab state from URL
  const currentTab = searchParams.get('tab') || 'products';

  // Product filter states (local for input, URL for query)
  const [productSearch, setProductSearch] = useState(searchParams.get('search') || '');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || '__all__');
  const productPage = Number(searchParams.get('productPage')) || 1;

  // Category filter states
  const categoryPage = Number(searchParams.get('categoryPage')) || 1;
  const categoryForm = useForm({
    defaultValues: { search: searchParams.get('categorySearch') || '' },
  });

  // Dialog states
  const [createProductOpen, setCreateProductOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<IProduct | null>(null);
  const [deleteProduct, setDeleteProduct] = useState<IProduct | null>(null);
  const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<IProductCategory | null>(null);
  const [deleteCategory, setDeleteCategory] = useState<IProductCategory | null>(null);

  // Fetch products — query key reads from URL params only
  const {
    data: productsData,
    isLoading: isLoadingProducts,
    refetch: refetchProducts,
  } = useQuery({
    queryKey: ['products', Object.fromEntries(searchParams), productPage],
    queryFn: () => {
      const search = searchParams.get('search');
      const category = searchParams.get('category');
      return productApi.findMany({
        filter: {
          search: search || undefined,
          category: category || undefined,
        },
        page: productPage,
        limit: 10,
      });
    },
  });

  // Fetch categories — query key reads from URL params only
  const {
    data: categoriesData,
    isLoading: isLoadingCategories,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: ['product-categories', searchParams.get('categorySearch') || '', categoryPage],
    queryFn: () => {
      const search = searchParams.get('categorySearch');
      return productApi.findManyCategories({
        filter: { search: search || undefined },
        page: categoryPage,
        limit: 10,
      });
    },
  });

  // Fetch all categories for filter dropdown
  const { data: allCategories = [] } = useQuery({
    queryKey: ['product-categories-all'],
    queryFn: () => productApi.findAllCategories(),
  });

  const updateProductActive = useMutation({
    mutationFn: async ({ product, active }: { product: IProduct; active: boolean }) => {
      const result = await productApi.update(product.id, { active });
      if (!result.success) {
        throw new Error(result.error?.global || 'Erro ao atualizar produto');
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao atualizar produto',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleProductActiveChange = (product: IProduct, active: boolean) => {
    if (updateProductActive.isPending) return;
    updateProductActive.mutate({ product, active });
  };

  // Handlers
  const handleTabChange = (tab: string) => {
    const params = new URLSearchParams(searchParams);
    if (tab === 'products') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    setSearchParams(params);
  };

  const handleProductSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    params.set('productPage', '1');
    if (productSearch) {
      params.set('search', productSearch);
    } else {
      params.delete('search');
    }
    if (categoryFilter && categoryFilter !== '__all__') {
      params.set('category', categoryFilter);
    } else {
      params.delete('category');
    }
    setSearchParams(params);
  };

  const handleCategorySearch = categoryForm.handleSubmit((values) => {
    const params = new URLSearchParams(searchParams);
    params.set('categoryPage', '1');
    if (values.search) {
      params.set('categorySearch', values.search);
    } else {
      params.delete('categorySearch');
    }
    setSearchParams(params);
  });

  const clearProductFilters = () => {
    setProductSearch('');
    setCategoryFilter('__all__');
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    params.delete('category');
    params.set('productPage', '1');
    setSearchParams(params);
  };

  const clearCategoryFilters = () => {
    categoryForm.reset({ search: '' });
    const params = new URLSearchParams(searchParams);
    params.delete('categorySearch');
    params.set('categoryPage', '1');
    setSearchParams(params);
  };

  const handleProductPageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('productPage', String(page));
    setSearchParams(params);
  };

  const handleCategoryPageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('categoryPage', String(page));
    setSearchParams(params);
  };

  const handleProductSuccess = () => {
    refetchProducts();
    queryClient.invalidateQueries({ queryKey: ['product-categories-all'] });
  };

  const handleCategorySuccess = () => {
    refetchCategories();
    queryClient.invalidateQueries({ queryKey: ['product-categories-all'] });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Produtos</h1>
          <p className="text-muted-foreground">Gerencie produtos e categorias do sistema</p>
        </div>
        {currentTab === 'products' && canCreateProduct && (
          <Button
            className="bg-black text-white hover:bg-gray-800"
            onClick={() => setCreateProductOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Produto
          </Button>
        )}
        {currentTab === 'categories' && canCreateCategory && (
          <Button
            className="bg-black text-white hover:bg-gray-800"
            onClick={() => setCreateCategoryOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Categoria
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="products">Produtos</TabsTrigger>
          <TabsTrigger value="categories">Categorias</TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Filtros de busca</CardTitle>
              <CardDescription>Preencha os filtros abaixo e clique em "Pesquisar"</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProductSearch} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      label="Pesquisar"
                      placeholder="Pesquise por nome ou SKU"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                    />
                  </div>
                  <div>
                    {/* biome-ignore lint/a11y/noLabelWithoutControl: Select renders its own interactive trigger */}
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Categoria
                    </label>
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Todas</SelectItem>
                        {allCategories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            <span className="flex items-center gap-2">
                              {category.name}
                              {category.type === ProductCategoryType.SYSTEM && (
                                <Badge variant="info-subtle" className="text-[10px] px-1.5 py-0">
                                  Sistema
                                </Badge>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={clearProductFilters}>
                    Limpar
                  </Button>
                  <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                    Pesquisar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {isLoadingProducts ? (
            <TableSkeleton rows={10} />
          ) : productsData ? (
            <ProductTable
              data={productsData}
              currentPage={productPage}
              onPageChange={handleProductPageChange}
              onEdit={setEditProduct}
              onDelete={setDeleteProduct}
              onActiveChange={handleProductActiveChange}
              activeChangeDisabled={updateProductActive.isPending}
            />
          ) : null}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Filtros de busca</CardTitle>
              <CardDescription>Preencha os filtros abaixo e clique em "Pesquisar"</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCategorySearch} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      label="Pesquisar"
                      placeholder="Pesquise por código ou nome"
                      {...categoryForm.register('search')}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={clearCategoryFilters}>
                    Limpar
                  </Button>
                  <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                    Pesquisar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {isLoadingCategories ? (
            <TableSkeleton rows={10} />
          ) : categoriesData ? (
            <ProductCategoryTable
              data={categoriesData}
              currentPage={categoryPage}
              onPageChange={handleCategoryPageChange}
              onEdit={setEditCategory}
              onDelete={setDeleteCategory}
            />
          ) : null}
        </TabsContent>
      </Tabs>

      {/* Product Dialogs */}
      <CreateProductDialog
        open={createProductOpen}
        onOpenChange={setCreateProductOpen}
        onSuccess={handleProductSuccess}
      />
      <EditProductDialog
        open={!!editProduct}
        onOpenChange={(open) => !open && setEditProduct(null)}
        product={editProduct}
        onSuccess={handleProductSuccess}
      />
      <DeleteProductDialog
        open={!!deleteProduct}
        onOpenChange={(open) => !open && setDeleteProduct(null)}
        product={deleteProduct}
        onSuccess={handleProductSuccess}
      />

      {/* Category Dialogs */}
      <CreateCategoryDialog
        open={createCategoryOpen}
        onOpenChange={setCreateCategoryOpen}
        onSuccess={handleCategorySuccess}
      />
      <EditCategoryDialog
        open={!!editCategory}
        onOpenChange={(open) => !open && setEditCategory(null)}
        category={editCategory}
        onSuccess={handleCategorySuccess}
      />
      <DeleteCategoryDialog
        open={!!deleteCategory}
        onOpenChange={(open) => !open && setDeleteCategory(null)}
        category={deleteCategory}
        onSuccess={handleCategorySuccess}
      />
    </motion.div>
  );
}
